import React, { useState, useRef, ChangeEvent, useEffect } from 'react';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { useLayers, IconButton, Button, Sprite } from '@agissc/ktag-map';
import { Datalist } from '@ktag/styleguide/dist/react';
import { UserMapConfiguration } from './api/useUserMapConfigurations';

interface ConfigurationListItemProps {
  configuration: UserMapConfiguration;
  isSelected: boolean;
  configurationChanged: (configuration: UserMapConfiguration) => void;
  configurationRemoved: () => void;
  onApplyConfiguration: () => void;
}

export const ConfigurationListItem = ({
  configuration,
  isSelected,
  configurationChanged,
  configurationRemoved,
  onApplyConfiguration,
}: ConfigurationListItemProps) => {
  const { t } = useTranslation('agisviewer');

  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(configuration.name);
  const [showEditIcon, setShowEditIcon] = useState(true);

  const inputRef = useRef<HTMLInputElement>(null);

  const layers = useLayers();

  const startEdit = () => setIsEditing(true);
  const stopEdit = () => {
    setIsEditing(false);
    setShowEditIcon(false);
    setTimeout(() => setShowEditIcon(true), 300);
  };

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) =>
    setEditedName(event.target.value);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      stopEdit();
    }
  };

  useEffect(() => {
    if (!isEditing && editedName !== configuration.name) {
      const name = DOMPurify.sanitize(editedName, { ALLOWED_TAGS: [] });
      configurationChanged({ ...configuration, name });
      setEditedName(name);
    }
  }, [isEditing, editedName]);

  useEffect(() => {
    if (!isEditing) return () => {};

    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        stopEdit();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditing]);

  if (!layers.data) return null;

  const formatLayerName = (layerId: string) =>
    layers.data[layerId]?.title ?? t('configuration_layer_not_found', { layer: layerId });

  const dataListItems = [
    {
      key: t('config_creationdate'),
      value: new Date(configuration.createdAt).toLocaleDateString('de-CH'),
    },
    {
      key: t('config_layers'),
      value: configuration.mapDefinitionValue.topicLayers
        .map((layer, index) => `${index === 0 ? '' : ', '}${formatLayerName(layer)}`)
        .join(''),
    },
    {
      key: t('config_background'),
      value: formatLayerName(configuration.mapDefinitionValue.basemap.selectedBasemapId),
    },
  ];

  return (
    <div
      className={classNames('configuration__item', {
        'configuration__item--active': isSelected,
      })}
    >
      {isSelected ? (
        <Datalist additionalClassNames="configuration__datalist" items={dataListItems} />
      ) : (
        <Button
          onClick={onApplyConfiguration}
          additionalClassNames="configuration__datalist"
        >
          <Datalist items={dataListItems} />
        </Button>
      )}

      {isEditing && (
        <div className="configuration__edit-name">
          <input
            ref={inputRef}
            className="form__input configuration__input"
            value={editedName}
            onChange={handleNameChange}
            onBlur={stopEdit}
            onKeyDown={handleKeyDown}
          />
          <IconButton onClick={stopEdit} icon="checkmark" title={t('config_confirm')} />
        </div>
      )}

      <div className="configuration__item-actions">
        {showEditIcon ? (
          <IconButton onClick={startEdit} icon="pencil" title={t('config_edit')} />
        ) : (
          <Sprite id="checkmark" additionalClassNames="configuration_saved-icon" />
        )}

        <IconButton
          additionalClassNames="iconButton"
          onClick={configurationRemoved}
          icon="wastebin"
          title={t('config_remove')}
        />
      </div>
    </div>
  );
};
